import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

function formatErr(detail) {
    if (!detail) return "Something went wrong";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
    if (detail?.msg) return detail.msg;
    return String(detail);
}

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [name, setName] = useState("");
    const [businessName, setBusinessName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await register({ email, password, name, business_name: businessName });
            toast.success("Gallery created");
            nav("/dashboard");
        } catch (e) {
            setErr(formatErr(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[80vh] grid lg:grid-cols-2">
            <div className="hidden lg:flex bg-[#E63946] text-white p-16 flex-col justify-between relative overflow-hidden">
                <div>
                    <div className="text-overline text-black mb-4">For builders & trades</div>
                    <h1 className="font-display text-5xl font-black tracking-tighter leading-none uppercase">
                        Build your<br />portfolio in<br />minutes.
                    </h1>
                </div>
                <ul className="space-y-3">
                    {["Free forever for builders", "Unlimited photos & projects", "QR code & sharable link", "Mobile-first portfolio pages"].map(
                        (b) => (
                            <li key={b} className="flex items-center gap-3 text-sm">
                                <span className="w-6 h-6 bg-black flex items-center justify-center text-[#E63946] font-black">✓</span>
                                {b}
                            </li>
                        )
                    )}
                </ul>
                <div className="absolute -bottom-32 -left-16 w-96 h-96 border-2 border-black rotate-6 pointer-events-none" />
            </div>

            <div className="flex items-center justify-center p-8 sm:p-16">
                <form onSubmit={submit} className="w-full max-w-md fade-in-up" data-testid="register-form">
                    <h2 className="font-display text-3xl font-black tracking-tighter leading-none uppercase mb-2">
                        Create gallery
                    </h2>
                    <p className="text-sm text-neutral-600 mb-8">
                        Already have one?{" "}
                        <Link to="/login" className="text-[#E63946] font-semibold hover:underline" data-testid="register-to-login">
                            Sign in
                        </Link>
                    </p>

                    <div className="grid grid-cols-2 gap-3 mb-5">
                        <div>
                            <label className="text-overline text-neutral-700 block mb-2">Your name</label>
                            <input
                                required
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="input-field"
                                placeholder="John Smith"
                                data-testid="register-name-input"
                            />
                        </div>
                        <div>
                            <label className="text-overline text-neutral-700 block mb-2">Business</label>
                            <input
                                value={businessName}
                                onChange={(e) => setBusinessName(e.target.value)}
                                className="input-field"
                                placeholder="Smith Builders"
                                data-testid="register-business-input"
                            />
                        </div>
                    </div>

                    <label className="text-overline text-neutral-700 block mb-2">Email</label>
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="input-field mb-5"
                        placeholder="you@buildersgallery.co.uk"
                        data-testid="register-email-input"
                    />

                    <label className="text-overline text-neutral-700 block mb-2">Password</label>
                    <input
                        type="password"
                        required
                        minLength={6}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="input-field"
                        placeholder="At least 6 characters"
                        data-testid="register-password-input"
                    />

                    {err && (
                        <div className="mt-3 text-sm text-[#E63946]" data-testid="register-error">
                            {err}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-accent w-full justify-center mt-6"
                        data-testid="register-submit-btn"
                    >
                        {loading ? "Creating…" : "Create my gallery"} <ArrowRight size={16} weight="bold" />
                    </button>
                </form>
            </div>
        </div>
    );
}
